/**
 * Wraps `ObsidianImportWindow` with a real `onConfirmImport` that
 * persists the parsed result into Convex via the existing `vaults`
 * API: `createVault` → `bulkInsertNotes` → `bulkInsertLinks` →
 * `bulkInsertAssets`. Without this host, the import UI parses files
 * into browser memory and the Confirm button is a no-op (the
 * prop was unwired).
 *
 * Mounted by `Win95Desktop` at `/obsidian-import` so it inherits the
 * portal context (TenantBrandingProvider). Vault is keyed by the
 * portal's `clientSlug` so subsequent reads via `listVaults` /
 * `listNotesByVault` find it.
 *
 * Assets bytes are dropped on commit — the schema only stores
 * metadata (path / mimeType / size / etag). Re-uploading to
 * SharePoint is a separate flow (F7 vault-export).
 *
 * 2026-05-27 UX fix: the prop now returns `Promise<CommitResult>` so
 * the window can show `committing` / `committed` / `error` phases.
 * The host RETHROWS on any failure (previously caught + swallowed to
 * console.error which produced the canonical "I clicked Confirm and
 * nothing happened" bug — see HANDOFF-2026-05-27-after-f18-polish.md).
 */

import { useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { ImportPath, ImportResult } from '../../lib/vault/importers/types';
import { useOptionalTenantBranding } from '../../lib/tenant/TenantBrandingContext';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import { readPortalSlugFromUrl } from '../../lib/tenant/portalSlugFromUrl';
import {
  ObsidianImportWindow,
  type ImportCommitResult,
} from './ObsidianImportWindow';

/** Map ImportPath (4-way) → vaults.SOURCE (3-way). zip/fsap/dragdrop
 *  all qualify as "local" file-system imports; only the SharePoint
 *  folder picker is "sharepoint". The Convex schema uses the narrower
 *  union so we collapse here at the boundary. */
function toVaultSource(path: ImportPath): 'local' | 'sharepoint' {
  return path === 'sharepoint' ? 'sharepoint' : 'local';
}

function nowIso(): string {
  return new Date().toISOString();
}

export function ObsidianImportWindowHost() {
  const branding = useOptionalTenantBranding();
  const [isDemo] = useShowcaseVisible();
  const demoSessionId = useShowcaseSessionId();
  const createVault = useMutation(api.vaults.createVault);
  const bulkInsertNotes = useMutation(api.vaults.bulkInsertNotes);
  const bulkInsertLinks = useMutation(api.vaults.bulkInsertLinks);
  const bulkInsertAssets = useMutation(api.vaults.bulkInsertAssets);

  const onConfirmImport = useCallback(
    async (result: ImportResult): Promise<ImportCommitResult> => {
      // Outside a portal context we still need a `clientSlug` to
      // partition the vault row. In showcase (signed-out demo) mode
      // we fall back to a synthetic slug; live mode requires a real
      // portal context so we don't accidentally write into the wrong
      // firm.
      // Three-step slug resolution, in order of trust:
      //   1. TenantBrandingProvider (most authoritative — backed by a
      //      real Convex row). May be null if WindowLayer renders this
      //      host outside the portal's provider subtree (see
      //      readSlugFromUrl comment for the nested-desktop scenario).
      //   2. URL path /c/<slug>/... — works as long as the user is on a
      //      portal route, regardless of which WindowProvider rendered
      //      us.
      //   3. Showcase demo — synthetic 'showcase' slug as a final
      //      fallback for signed-out demo visitors.
      let clientSlug: string;
      if (branding) {
        clientSlug = branding.slug;
      } else {
        const slugFromUrl = readPortalSlugFromUrl();
        if (slugFromUrl) {
          clientSlug = slugFromUrl;
          console.warn(
            `[ObsidianImportWindowHost] branding context missing; using slug "${slugFromUrl}" from URL path. ` +
              `Check that <TenantBrandingProvider> is in scope at the WindowLayer site.`,
          );
        } else if (isDemo) {
          clientSlug = 'showcase';
        } else {
          const msg =
            'No portal context — open this window from inside a portal (/c/<slug>/desktop) so the import can be saved.';
          console.error('[ObsidianImportWindowHost]', msg);
          throw new Error(msg);
        }
      }

      const demoArgs = isDemo ? { demoSessionId } : {};
      const vaultSource = toVaultSource(result.sourceKind);
      const vaultName = result.sourceLabel || `Imported vault ${nowIso()}`;

      const totalLinks = result.notes.reduce(
        (acc, n) => acc + n.wikilinks.length + n.markdownLinks.length,
        0,
      );
      console.info(
        `[ObsidianImportWindowHost] persisting vault clientSlug=${clientSlug} source=${vaultSource} notes=${result.notes.length} links=${totalLinks} assets=${result.assets.length}`,
      );

      let vaultId: string;
      try {
        vaultId = await createVault({
          clientSlug,
          name: vaultName,
          source: vaultSource,
          ...demoArgs,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(
          `[ObsidianImportWindowHost] createVault failed: ${msg}`,
          err,
        );
        throw new Error(`createVault failed: ${msg}`);
      }

      // Map ParsedNote → vaultNotes row shape. ParsedNote.wikilinks
      // and .markdownLinks are already string arrays (deduplicated).
      // etag/lastModified are required by the schema; the parser
      // doesn't carry them so we synthesize.
      const noteRows = result.notes.map((n) => ({
        path: n.path,
        name: n.name,
        body: n.body,
        frontmatterJson: JSON.stringify(n.frontmatter),
        wikilinks: n.wikilinks,
        markdownLinks: n.markdownLinks,
        tags: n.tags,
        etag: '',
        lastModified: nowIso(),
      }));

      let notesInserted = 0;
      if (noteRows.length > 0) {
        try {
          const res = await bulkInsertNotes({
            vaultId,
            clientSlug,
            source: vaultSource,
            notes: noteRows,
            ...demoArgs,
          });
          notesInserted = res.inserted;
          console.info(
            `[ObsidianImportWindowHost] bulkInsertNotes inserted=${res.inserted}`,
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.error(
            `[ObsidianImportWindowHost] bulkInsertNotes failed: ${msg}`,
            err,
          );
          throw new Error(`bulkInsertNotes failed: ${msg}`);
        }
      }

      // Links flattened from per-note wikilinks + markdownLinks.
      const linkRows: Array<{
        fromNotePath: string;
        toNotePath: string;
        kind: 'wikilink' | 'mdlink';
      }> = [];
      for (const note of result.notes) {
        for (const target of note.wikilinks) {
          linkRows.push({
            fromNotePath: note.path,
            toNotePath: target,
            kind: 'wikilink',
          });
        }
        for (const url of note.markdownLinks) {
          linkRows.push({
            fromNotePath: note.path,
            toNotePath: url,
            kind: 'mdlink',
          });
        }
      }
      let linksInserted = 0;
      if (linkRows.length > 0) {
        try {
          const res = await bulkInsertLinks({
            vaultId,
            clientSlug,
            links: linkRows,
            ...demoArgs,
          });
          linksInserted = res.inserted;
          console.info(
            `[ObsidianImportWindowHost] bulkInsertLinks inserted=${res.inserted}`,
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.error(
            `[ObsidianImportWindowHost] bulkInsertLinks failed: ${msg}`,
            err,
          );
          throw new Error(`bulkInsertLinks failed: ${msg}`);
        }
      }

      // Asset metadata only — bytes are dropped. Re-upload happens
      // via the F7 vault-export-to-SharePoint flow.
      const assetRows = result.assets.map((a) => ({
        path: a.path,
        mimeType: a.mimeType ?? 'application/octet-stream',
        sizeBytes: a.sizeBytes,
        etag: a.etag ?? '',
        lastModified:
          a.lastModifiedMs !== undefined
            ? new Date(a.lastModifiedMs).toISOString()
            : nowIso(),
      }));
      let assetsInserted = 0;
      if (assetRows.length > 0) {
        try {
          const res = await bulkInsertAssets({
            vaultId,
            clientSlug,
            assets: assetRows,
            ...demoArgs,
          });
          assetsInserted = res.inserted;
          console.info(
            `[ObsidianImportWindowHost] bulkInsertAssets inserted=${res.inserted}`,
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.error(
            `[ObsidianImportWindowHost] bulkInsertAssets failed: ${msg}`,
            err,
          );
          throw new Error(`bulkInsertAssets failed: ${msg}`);
        }
      }

      console.info(
        `[ObsidianImportWindowHost] import committed vaultId=${vaultId} notes=${notesInserted} links=${linksInserted} assets=${assetsInserted}`,
      );
      return {
        vaultId,
        counts: {
          notes: notesInserted,
          links: linksInserted,
          assets: assetsInserted,
        },
      };
    },
    [
      branding,
      isDemo,
      demoSessionId,
      createVault,
      bulkInsertNotes,
      bulkInsertLinks,
      bulkInsertAssets,
    ],
  );

  return <ObsidianImportWindow onConfirmImport={onConfirmImport} />;
}
